import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { format } from 'date-fns';
import type { Transaction, TransactionSource } from '@/types/asset-import.types';

interface CellRendererContext {
  tx: Transaction;
  getDisplayValue: (tx: Transaction, field: keyof Transaction) => string;
  handleEditField: (txId: string, field: keyof Transaction, value: string) => void;
  availableCategories: string[];
  formatCurrency: (value: number | null | undefined) => string;
  canSplitTransaction: (tx: Transaction) => boolean;
  splitTransactions: Set<string>;
  handleSplitToggle: (txId: string, enabled: boolean) => void;
  getTransactionTypeLabel: (type: string) => string;
  getTransactionSourceLabel: (source: TransactionSource) => string;
}

export interface UseTransactionCellRendererOptions {
  getDisplayValue: (tx: Transaction, field: keyof Transaction) => string;
  handleEditField: (txId: string, field: keyof Transaction, value: string) => void;
  availableCategories: string[];
  formatCurrency: (value: number | null | undefined) => string;
  canSplitTransaction: (tx: Transaction) => boolean;
  splitTransactions: Set<string>;
  handleSplitToggle: (txId: string, enabled: boolean) => void;
  getTransactionTypeLabel: (type: string) => string;
  getTransactionSourceLabel: (source: TransactionSource) => string;
}

type CellRenderer = (columnId: string, context: CellRendererContext) => ReactNode;

const renderTextCell = (columnId: string, value: string | null | undefined, className = ''): ReactNode => (
  <TableCell key={columnId} className={`px-4 py-3 text-slate-700 dark:text-slate-300 ${className}`}>
    {value ?? '-'}
  </TableCell>
);

const renderCurrencyCell = (columnId: string, value: number | null | undefined, formatCurrency: (v: number | null | undefined) => string): ReactNode => (
  <TableCell key={columnId} className="px-4 py-3 text-right text-slate-700 dark:text-slate-300 tabular-nums">
    {formatCurrency(value)}
  </TableCell>
);

const renderTransactionReference: CellRenderer = (columnId, { tx }) => (
  <TableCell key={columnId} className="px-4 py-3 text-slate-700 dark:text-slate-300 font-mono text-xs font-medium">
    {tx.transactionReference}
  </TableCell>
);

const renderTransactionDate: CellRenderer = (columnId, { tx }) => {
  const date = tx.transactionDate ? new Date(tx.transactionDate) : null;
  const isValidDate = date && !isNaN(date.getTime());
  return (
    <TableCell key={columnId} className="px-4 py-3 text-slate-700 dark:text-slate-300 tabular-nums">
      {isValidDate ? format(date, 'dd/MM/yyyy') : '-'}
    </TableCell>
  );
};

const renderTransactionType: CellRenderer = (columnId, { tx, getTransactionTypeLabel }) => (
  <TableCell key={columnId} className="px-4 py-3 text-slate-700 dark:text-slate-300">
    <Badge variant="outline" className="text-xs">
      {getTransactionTypeLabel(tx.transactionType)}
    </Badge>
  </TableCell>
);

const renderTransactionSource: CellRenderer = (columnId, { tx, getTransactionSourceLabel }) => (
  <TableCell key={columnId} className="px-4 py-3 text-slate-700 dark:text-slate-300">
    {getTransactionSourceLabel(tx.transactionSource)}
  </TableCell>
);

const renderAssetCode: CellRenderer = (columnId, { tx, getDisplayValue, handleEditField }) => {
  const isDisabled = !tx.canCreateAsset || tx.status === 'already-used';
  const currentValue = getDisplayValue(tx, 'editedAssetCode') || '';
  return (
    <TableCell key={columnId} className="px-2 py-2">
      <Input
        value={currentValue}
        onChange={(e) => handleEditField(tx.id, 'editedAssetCode', e.target.value)}
        disabled={isDisabled}
        className="h-8 text-sm font-medium"
        placeholder="Auto-generated"
      />
    </TableCell>
  );
};

const renderDescription: CellRenderer = (columnId, { tx, getDisplayValue, handleEditField }) => {
  const isDisabled = !tx.canCreateAsset || tx.status === 'already-used';
  return (
    <TableCell key={columnId} className="px-2 py-2">
      <Input
        value={getDisplayValue(tx, 'description')}
        onChange={(e) => handleEditField(tx.id, 'description', e.target.value)}
        disabled={isDisabled}
        className="h-8 text-sm"
        placeholder="Asset description"
      />
    </TableCell>
  );
};

const renderCategory: CellRenderer = (columnId, { tx, getDisplayValue, handleEditField, availableCategories }) => {
  const isDisabled = !tx.canCreateAsset || tx.status === 'already-used';
  const currentValue = getDisplayValue(tx, 'category') || '';
  return (
    <TableCell key={columnId} className="px-2 py-2">
      <Select
        value={currentValue}
        onValueChange={(value: string) => handleEditField(tx.id, 'category', value)}
        disabled={isDisabled}
      >
        <SelectTrigger className="h-8 text-sm">
          <SelectValue placeholder="Select category" />
        </SelectTrigger>
        <SelectContent>
          {availableCategories.map((cat) => (
            <SelectItem key={cat} value={cat}>
              {cat}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </TableCell>
  );
};

const renderSplit: CellRenderer = (columnId, { tx, canSplitTransaction, splitTransactions, handleSplitToggle }) => {
  const canSplit = canSplitTransaction(tx);
  const isSplitEnabled = splitTransactions.has(tx.id);
  return (
    <TableCell key={columnId} className="px-4 py-3">
      {canSplit ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="inline-flex">
                <Switch
                  checked={isSplitEnabled}
                  onCheckedChange={(checked) => handleSplitToggle(tx.id, checked)}
                  aria-label={`Split transaction into ${tx.quantity} individual assets`}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{isSplitEnabled ? `Will create ${tx.quantity} assets` : `Split into ${tx.quantity} assets`}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <span className="text-xs text-muted-foreground">-</span>
      )}
    </TableCell>
  );
};

const getStatusBadge = (status: Transaction['status']): ReactNode => {
  if (status === 'already-used') {
    return (
      <Badge className="bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400">
        Already Used
      </Badge>
    );
  }
  if (status === 'eligible') {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
        Eligible
      </Badge>
    );
  }
  return (
    <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
      Excluded
    </Badge>
  );
};

const renderStatus: CellRenderer = (columnId, { tx }) => (
  <TableCell key={columnId} className="px-4 py-3">
    {getStatusBadge(tx.status)}
  </TableCell>
);

const cellRenderers: Record<string, CellRenderer> = {
  transactionReference: renderTransactionReference,
  transactionDate: renderTransactionDate,
  transactionType: renderTransactionType,
  transactionSource: renderTransactionSource,
  assetCode: renderAssetCode,
  description: renderDescription,
  category: renderCategory,
  split: renderSplit,
  status: renderStatus,
};

const simpleCellConfig: Record<string, { field: keyof Transaction; className?: string }> = {
  netValue: { field: 'netValue' },
  vatAmount: { field: 'vatAmount' },
  grossValue: { field: 'grossValue' },
  supplier: { field: 'supplier' },
  analysisCode: { field: 'analysisCode', className: 'font-mono text-xs' },
  nominalAccount: { field: 'nominalAccount', className: 'font-mono text-xs' },
  costCentre: { field: 'costCentre' },
  project: { field: 'project', className: 'font-mono text-xs' },
  serialNumber: { field: 'serialNumber', className: 'font-mono text-xs' },
};

export function useTransactionCellRenderer(options: UseTransactionCellRendererOptions) {
  const {
    getDisplayValue,
    handleEditField,
    availableCategories,
    formatCurrency,
    canSplitTransaction,
    splitTransactions,
    handleSplitToggle,
    getTransactionTypeLabel,
    getTransactionSourceLabel,
  } = options;

  const renderCell = useCallback((columnId: string, tx: Transaction): ReactNode => {
    const context: CellRendererContext = {
      tx,
      getDisplayValue,
      handleEditField,
      availableCategories,
      formatCurrency,
      canSplitTransaction,
      splitTransactions,
      handleSplitToggle,
      getTransactionTypeLabel,
      getTransactionSourceLabel,
    };

    const renderer = cellRenderers[columnId];
    if (renderer) {
      return renderer(columnId, context);
    }

    const simpleConfig = simpleCellConfig[columnId];
    if (simpleConfig) {
      const value = tx[simpleConfig.field];
      if (columnId === 'netValue' || columnId === 'vatAmount' || columnId === 'grossValue') {
        return renderCurrencyCell(columnId, value as number | null | undefined, formatCurrency);
      }
      if (columnId === 'quantity') {
        return (
          <TableCell key={columnId} className="px-4 py-3 text-right text-slate-700 dark:text-slate-300 tabular-nums">
            {tx.quantity ?? '-'}
          </TableCell>
        );
      }
      return renderTextCell(columnId, value as string | null | undefined, simpleConfig.className);
    }

    if (columnId === 'quantity') {
      return (
        <TableCell key={columnId} className="px-4 py-3 text-right text-slate-700 dark:text-slate-300 tabular-nums">
          {tx.quantity ?? '-'}
        </TableCell>
      );
    }

    return null;
  }, [
    getDisplayValue,
    handleEditField,
    availableCategories,
    formatCurrency,
    canSplitTransaction,
    splitTransactions,
    handleSplitToggle,
    getTransactionTypeLabel,
    getTransactionSourceLabel,
  ]);

  return { renderCell };
}
